import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Calendar/ui/dialog";
import { Button } from "@/components/Calendar/ui/button";
import { Input } from "@/components/Calendar/ui/input";
import { Label } from "@/components/Calendar/ui/label";
import { Textarea } from "@/components/Calendar/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Calendar/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Calendar/ui/popover";
import { CalendarIcon, Clock, User, AlertCircle, Loader2 } from "lucide-react";
import {
  type Task,
  taskCategories,
  taskPriorities,
} from "@/components/Calendar/scheduleData";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import {
  addAndUpdateTaskEvent,
  refreshScheduleEventsSilently,
} from "@/dispatchers/scheduleEventsDispatchers.js";

interface TaskModalProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave: (taskData: any) => void;
  initialData?: Partial<Task>;
  isDarkMode?: boolean;
  initialDate?: string;
  initialAssignee?: string;
  pharmacists?: string[];
  dynamicResources?: Array<{ id: string; name: string }>;
  currentPharmacyId?: string;
}

export function TaskModal({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onSave,
  initialData,
  isDarkMode = false,
  initialDate,
  initialAssignee,
  pharmacists = [],
  dynamicResources = [],
  currentPharmacyId = "",
}: TaskModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [dueTime, setDueTime] = useState("");
  const [priority, setPriority] = useState<Task["priority"]>("medium");
  const [category, setCategory] = useState<Task["category"]>("administrative");
  const [notes, setNotes] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper to parse local YYYY-MM-DD or date strings without timezone shifts
  const parseLocalDate = (dateStr?: string) => {
    if (!dateStr) return undefined;
    if (typeof dateStr === "string" && dateStr.includes("-")) {
      const cleanStr = dateStr.split("T")[0];
      const parts = cleanStr.split("-").map(Number);
      if (parts.length === 3 && !parts.some(isNaN)) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  // Cleanup effect to remove any stuck portals/overlays
  useEffect(() => {
    if (!open) {
      setDatePickerOpen(false);
      setIsSubmitting(false);

      const cleanup = () => {
        const portals = document.querySelectorAll(
          "[data-radix-popper-content-wrapper]",
        );
        portals.forEach((portal) => {
          const content = portal.querySelector("[data-state]");
          if (!content || content.getAttribute("data-state") !== "open") {
            if (portal.parentElement) {
              portal.parentElement.removeChild(portal);
            }
          }
        });

        document.body.style.pointerEvents = "";

        const style = document.body.getAttribute("style");
        if (style && style.includes("overflow")) {
          document.body.style.overflow = "";
        }
      };

      const timer = setTimeout(cleanup, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Initialize form with initial data or defaults
  useEffect(() => {
    if (open) {
      setIsSubmitting(false);
      setTitle(initialData?.title || "");
      setDescription(initialData?.description || "");

      const rawAssignee = initialData?.assignedTo || initialAssignee || "";
      let resolvedAssignee = rawAssignee;
      if (dynamicResources && dynamicResources.length > 0) {
        const found = dynamicResources.find(
          (r) =>
            r.id === rawAssignee ||
            r.name.toLowerCase() === rawAssignee.toLowerCase(),
        );
        if (found) {
          resolvedAssignee = found.id;
        } else if (!rawAssignee) {
          resolvedAssignee = dynamicResources[0].id;
        }
      }
      setAssignedTo(resolvedAssignee);

      const parsedDate = parseLocalDate(initialData?.dueDate || initialDate);
      setDueDate(parsedDate || new Date());

      setDueTime(initialData?.dueTime || "09:00");
      setPriority(initialData?.priority || "medium");
      setCategory(initialData?.category || "administrative");
      setNotes(
        initialData?.notes ||
          (initialData as any)?.AdditionalNotes ||
          (initialData as any)?.additionalNotes ||
          "",
      );
      setDatePickerOpen(false);
    } else {
      setDatePickerOpen(false);
      setIsSubmitting(false);

      const timer = setTimeout(() => {
        setTitle("");
        setDescription("");
        setAssignedTo("");
        setDueDate(new Date());
        setDueTime("09:00");
        setPriority("medium");
        setCategory("administrative");
        setNotes("");
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open, initialData, initialDate, initialAssignee, dynamicResources]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !assignedTo || !dueDate) {
      alert("Please fill in all required fields");
      return;
    }

    // Past date validation
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDueDate = new Date(dueDate);
    checkDueDate.setHours(0, 0, 0, 0);

    if (checkDueDate < today) {
      alert(
        "Due date cannot be set to a past date. Please select today or a future date.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      setDatePickerOpen(false);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const [hours, minutes] = (dueTime || "09:00").split(":").map(Number);
      // const startDateObj = new Date(dueDate);
      // startDateObj.setHours(hours || 0, minutes || 0, 0, 0);

      // const endDateObj = new Date(startDateObj);
      // endDateObj.setMinutes(endDateObj.getMinutes() + 30);

      const startDateObj = new Date(dueDate);
      startDateObj.setHours(hours || 0, minutes || 0, 0, 0);

      // Add 1 hour
      startDateObj.setHours(startDateObj.getHours());

      const endDateObj = new Date(startDateObj);
      endDateObj.setMinutes(endDateObj.getMinutes() + 30);

      const startDateISO = startDateObj.toISOString();
      const endDateISO = endDateObj.toISOString();

      const selectedResource = dynamicResources.find(
        (r) => r.id === assignedTo,
      );
      const assignedToName = selectedResource
        ? selectedResource.name
        : assignedTo;
      const eventId = initialData?.id || uuidv4();

      const scheduleEventPayload: any = {
        id: eventId,
        _id: eventId,
        ResourceType: 1,
        PharmacyID: currentPharmacyId || "",
        ResourceId: assignedTo,
        CreatedAt: initialData?.createdAt || new Date().toISOString(),
        CalenderName: title.trim(),
        StartDate: startDateISO,
        EndDate: endDateISO,
        Duration: 30,
        DurationUnit: "minutes",
        Name: title.trim(),
        ScheduleEventType: 7,
        Email: assignedTo.includes("@") ? assignedTo : null,
        EventStatus: 1,
        Priority: priority,
        Category: category,
        title: title.trim(),
        description: description.trim(),
        assignedTo: assignedTo,
        assignedToName: assignedToName,
        dueDate: format(dueDate, "yyyy-MM-dd"),
        dueTime,
        additionalNotes: notes.trim(),
        assignedBy: "Current User",
        status: initialData?.status || "todo",
      };

      const appointment = {
        id: eventId,
        startDate: `${format(startDateObj, "yyyy-MM-dd'T'HH:mm:ss")}Z`,
        endDate: `${format(endDateObj, "yyyy-MM-dd'T'HH:mm:ss")}Z`,
        duration: 30,
        durationUnit: "m",
        cls: priority,
        patientID: "",
        name: title.trim() || "",
        recurrenceRule: "",
        exceptionDates: [],
        resourceId: assignedTo,
        allDay: false,
        scheduleEventType: 7,
        serviceID: "",
        email: assignedTo.includes("@") ? assignedTo : "",
        mobile: "",
        confirmationMethod: "",
        RoomId: "",
        createdAt: initialData?.createdAt || new Date().toISOString(),
        priority: priority,
        category: category,
        description: description.trim(),
        additionalNotes: notes.trim(),
      };

      if (currentPharmacyId) {
        await addAndUpdateTaskEvent(appointment, currentPharmacyId);
        await refreshScheduleEventsSilently(currentPharmacyId);
      }

      if (onSave) {
        await onSave(scheduleEventPayload);
      }
      setOpen(false);
    } catch (err) {
      console.error("Failed to save task:", err);
      alert("An error occurred while saving the task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityConfig = (p: Task["priority"]) => {
    return taskPriorities.find((tp) => tp.value === p) || taskPriorities[1];
  };

  const getCategoryConfig = (c: Task["category"]) => {
    return taskCategories.find((tc) => tc.value === c) || taskCategories[0];
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (isSubmitting) return; // Prevent closing while submitting
    if (!newOpen) {
      setDatePickerOpen(false);
    }
    setOpen(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
        }`}
      >
        <DialogHeader>
          <DialogTitle className={isDarkMode ? "text-white" : "text-gray-900"}>
            {initialData?.id ? "Edit Task" : "Create New Task"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <div className="space-y-2">
            <Label
              htmlFor="title"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Task Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              disabled={isSubmitting}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              required
              className={
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white"
              }
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="description"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              disabled={isSubmitting}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add more details about this task"
              rows={3}
              className={
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white"
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="assignedTo"
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Assign To <span className="text-red-500">*</span>
              </Label>
              <Select
                value={assignedTo}
                onValueChange={setAssignedTo}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  className={
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }
                >
                  <SelectValue placeholder="Select team member">
                    {assignedTo && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {dynamicResources?.find((r) => r.id === assignedTo)
                          ?.name ||
                          pharmacists?.find((p) => p === assignedTo) ||
                          assignedTo}
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className={`z-[999999] ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                >
                  {(dynamicResources && dynamicResources.length > 0
                    ? dynamicResources
                    : (pharmacists || []).map((p) => ({ id: p, name: p }))
                  ).map((res) => (
                    <SelectItem key={res.id} value={res.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {res.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="priority"
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Priority
              </Label>
              <Select
                value={priority}
                onValueChange={(val) => setPriority(val as Task["priority"])}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  className={
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }
                >
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${getPriorityConfig(priority).color}`}
                      ></span>
                      {getPriorityConfig(priority).label}
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className={`z-[999999] ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                >
                  {taskPriorities.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${p.color}`}
                        ></span>
                        {p.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Due Date */}
            <div className="space-y-2">
              <Label
                htmlFor="dueDateInput"
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Due Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dueDateInput"
                type="date"
                disabled={isSubmitting}
                min={format(new Date(), "yyyy-MM-dd")}
                value={dueDate ? format(dueDate, "yyyy-MM-dd") : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    const [y, m, d] = val.split("-").map(Number);
                    const selectedDate = new Date(y, m - 1, d);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (selectedDate < today) {
                      alert("Due date cannot be set to a past date.");
                      return;
                    }
                    setDueDate(selectedDate);
                  }
                }}
                className={`w-full ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white text-gray-900"
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="dueTime"
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Due Time
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="dueTime"
                  type="time"
                  disabled={isSubmitting}
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  className={`pl-10 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="category"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Category
            </Label>
            <Select
              value={category}
              onValueChange={(val) => setCategory(val as Task["category"])}
              disabled={isSubmitting}
            >
              <SelectTrigger
                className={
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white"
                }
              >
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <span>{getCategoryConfig(category).icon}</span>
                    {getCategoryConfig(category).label}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent
                className={`z-[999999] ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white"
                }`}
              >
                {taskCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="notes"
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Additional Notes
            </Label>
            <Textarea
              id="notes"
              value={notes}
              disabled={isSubmitting}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information or context"
              rows={2}
              className={
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white"
              }
            />
          </div>

          <div
            className={`flex items-start gap-2 p-3 rounded-lg ${
              isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
            }`}
          >
            <AlertCircle
              className={`w-4 h-4 mt-0.5 ${
                isDarkMode ? "text-blue-400" : "text-blue-600"
              }`}
            />
            <p
              className={`text-xs ${
                isDarkMode ? "text-blue-300" : "text-blue-600"
              }`}
            >
              Fields marked with <span className="text-red-500">*</span> are
              required
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => {
                setDatePickerOpen(false);
                setOpen(false);
              }}
              className={
                isDarkMode
                  ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                  : ""
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="!bg-slate-900 hover:!bg-slate-800 !text-white border-0 min-w-[120px]"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {initialData?.id ? "Updating..." : "Adding..."}
                </span>
              ) : initialData?.id ? (
                "Update Task"
              ) : (
                "Create Task"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
